<script>
	import {createEventDispatcher, getContext, setContext} from 'svelte';
	import {writable} from 'svelte/store';
	import {Texture, General, Perm, AW, SurveyInfo, SurveyInfoMUDesc} from './report-sections';
	import {default as SiblingMap} from './maps/SiblingMap.svelte';
	import {Report, Toolbar, LinkButton} from '@manaakiwhenua/frag-svelte/src/index';
	import {default as Footer} from './report-sections/Footer.svelte';

	import {MaoriFactsheetAPI, SMUFactsheetsAPI} from "./api";
	import {keysToLowerCase} from "./report-sections/util";
	import {uuid} from "@manaakiwhenua/frag-svelte/src/uuid";
	import NeverReady from "./report-sections/NeverReady.svelte";
	import Landform from "./maoriReportSections/Landform.svelte";

	export let gisLayer;
	export let soilMapUnitCode;
	export let reportLink = '';
	export let pinCoordinate;
	export let printLink = '';
	export let licence = undefined;
	export let factsheetApiUrl = undefined;

	const readyStore = getContext('readyStore') || writable(new Map())
	const printing = getContext('printStore') || writable(false)
	const instanceId = uuid();

	setContext('printStore', printing)
	setContext('readyStore', readyStore)

	$: api = new SMUFactsheetsAPI(factsheetApiUrl);

	$: siblingsRequest = api.siblings(gisLayer, soilMapUnitCode).then(data => data.map(keysToLowerCase));
	$: polygonsRequest = api.envelopes(gisLayer, soilMapUnitCode);
	$: surveyInfoRequest = api.surveyinfo(gisLayer, soilMapUnitCode).then(data => data.map(keysToLowerCase));

	$: categories = [
		{
			title: "Texture",
			topics: [{
				component: Texture
			}]
		},
		{
			title: "Permeability",
			topics: [{
				component: Perm
			}]
		},
		{
			title: "Available Water",
			topics: [{
				component: AW
			}]
		}
	];

	const dispatch = createEventDispatcher();

	function closeReport() {
		dispatch('closeReport');
	}

	const linkButtonClass = 'btn btn-info no-box-shadow';

	$: landFormParams = {
		gisLayer, soilMapUnitCode
	}
	$: maoriApi = new MaoriFactsheetAPI(factsheetApiUrl);
	$: dataLandform = maoriApi.landform(landFormParams).then(resp => Object.entries(resp).length ? resp : null);
</script>

<style>
	[slot=topic] {
		min-height: 780px;
	}

	[slot=header] {
		padding: 0 1em;
	}

	@media print {
		[slot=topic] {
			break-before: page;
			break-inside: avoid;
		}

		:global(.category-title) {
			display: none !important;
		}
	}

	a.print-link {
		display: inline-block;
		color: white;
	}

	a.print-link:hover {
		text-decoration: none;
	}
</style>

<Report {categories}>
	<div slot="toolbar">
		<Toolbar printEnabled={!printLink}>
			<div slot="extraButtons">
				<LinkButton link={reportLink} buttonClass={linkButtonClass}/>
				{#if printLink}
					<a href={printLink}
					   class="print-link"
					   target="_blank"
					   title="Printable version of this factsheet">
						<i class="icon icon-print"></i>
					</a>
				{/if}
			</div>
			<i slot="closeButton" role="button" class="close-button icon icon-close_large" on:click={closeReport}></i>
		</Toolbar>
	</div>

	<div slot="header">
		<h1 class="heading">Soil map unit factsheet</h1>

		{#await polygonsRequest}
			<p>loading soil map unit polygons...</p>
		{:then polygons}
			{#if polygons.length}
				<div class="sibling-map-outer">
					<SiblingMap siblingPolygons={polygons} {gisLayer} {soilMapUnitCode} {pinCoordinate}/>
				</div>
			{/if}
		{:catch ex}
		{/await}

		{#await dataLandform}
		{:then landformData}
			{#if landformData}
				<h2>Landform</h2>
				<Landform dataLandformMapped={dataLandform} descriptionBelow={false} />
			{/if}
		{/await}

		{#await siblingsRequest}
			<p>loading general information...</p>
		{:then dataMapUnit}
			<General {dataMapUnit}/>
			{#await surveyInfoRequest}
				<p>checking for survey info...</p>
			{:then dataSurveyInfo}
				<SurveyInfo {dataSurveyInfo}/>
				<SurveyInfoMUDesc {dataSurveyInfo}/>
			{:catch ex}
			{/await}
		{:catch ex}
			<p>Failed to load sibling data. Refresh the page to try again.</p>
		{/await}
	</div>
	<div slot="topic" let:topic>
		{#await siblingsRequest}
			<p>loading sibling data...</p>
			<NeverReady/>
		{:then dataMapUnit}
			<svelte:component this={topic.component} {dataMapUnit}/>
		{:catch ex}
		{/await}
	</div>

	<div slot="footer">
		<Footer {licence}/>
	</div>
</Report>
